/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useState } from 'react'
import _ from 'lodash'
import { isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import { Popover } from 'antd'
import Chat from '@views/Popup/Chat'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import {
  appScrollTo,
  formatNumber,
  formatNumberTicketCount,
  isAccountForTesting,
  isLiveScoreRecommender,
  loadOrReload,
  makeActivityGageConfig,
  makeRemainingActivityScore,
  tooltipTicketCount,
  toShortNumber,
} from '@utils/utils'
import PieChart from '@views/Am4Chart/PieChart'

// icon
import { BaseballIco, BasketballIco, IcehockeyIco, SoccerIco, VolleyballIco } from '@shared/Icon/SportsIcon'
import { CrossIco, HandiIco, LiveIco, OddsIco, SpecialIco } from '@shared/Icon/ClubIcon'
import { LogoIco } from '@shared/Icon/LogoIcon'
import { ChangeProfileIco, EditIco, ItemListIco, PopoverIco } from '@shared/Icon/EtcIcon'
import MyInfoImg from '@assets/images/bg/my-info-bg.png'
import LiveScoreImg from '@assets/images/icon/etc_icon/live-score-ico.png'
import { ArrowIco } from '@shared/Icon/ArrowIcon'
import { SettingIco } from '@shared/Icon/Chat'
import NavBar from '@views/Navbar'
import { BET_TICKET_TYPE, EMPTY_ICON } from '@constants/index'
import { LogoutIco, TelegramIco } from '@shared/Icon/MenuIcon'
import { DiaIco, GmoneyIco, HeartIco, ScashIco } from '@shared/Icon/MoneyIcon'
import FriendsInvitationBtnImg from '@assets/images/btn/friends-invitation-btn.png'
import FriendsInvitationBubbleTxtImg from '@assets/images/text/friend-invitation-bubble-txt.png'
// import RatingSImg from '@assets/images/icon/my_page_icon/rating-S.png'
// import RatingAImg from '@assets/images/icon/my_page_icon/rating-A.png'
// import RatingBImg from '@assets/images/icon/my_page_icon/rating-B.png'
// import RatingCImg from '@assets/images/icon/my_page_icon/rating-C.png'
// import RatingDImg from '@assets/images/icon/my_page_icon/rating-D.png'
// css
import { CenterTabWrap, PageBgStyle, TitleWrap } from '@styles/base.style'
import { BackBtn, Button } from '@styles/button.style'
import sCashBtn from '@assets/images/btn/scash-btn-mypage.png'
import { AdvEmpty } from '@components/AdvEmpty'
import { EXTRA_ODDS_ITEM_PROPERTY } from 'adv-protocol/src/GameCommon'
import NickDrawer from './NickDrawer'
import { UserDetailsWrap, activityScoreBtnStyle } from './index.style'
import { AdvButton } from '@components/Button'
// import WriteMessageIco from '../../shared/Icon/MessageIcon/WriteMessageIco'
// import CircleCheckIco from '../../shared/Icon/EtcIcon/CircleCheckIco'
import PopupUtils from '@utils/popup-utils'
import StorageUtils from '@utils/storage-utils'
import dayjs from 'dayjs'
import BlockManagementIco from '@shared/Icon/MenuIcon/BlockManagementIco'
import { Tabs } from 'antd-mobile'
import { Models } from 'rmc-tabs/lib/Models'
import classNames from 'classnames'
import WrenchIco from '@shared/Icon/MenuIcon/WrenchIco'
import { UserTitle } from '@components/UserTitle'
import DialogUtils from '@utils/dialog-utils'
import AdvImg from '@components/AdvImg'
import ManagedUserButton from '@components/Button/ManagedUserButton'
import FavoriteBorderIco from '@shared/Icon/EtcIcon/FavoriteBorderIco'
import RankingPopupButton from '@views/Popup/RankingPopup/Button'
// import DialogUtils from '@utils/dialog-utils'
// import { ACTIVITY_RANK_TYPE } from '@v2/constants/activity'

const GOD_BLESS_ITEM_STYLE_CLASS_MAP = {
  [EXTRA_ODDS_ITEM_PROPERTY.APHRODITE]: 'aphrodite',
  [EXTRA_ODDS_ITEM_PROPERTY.ATHENA]: 'athena',
  [EXTRA_ODDS_ITEM_PROPERTY.APOLLON]: 'apollon',
  [EXTRA_ODDS_ITEM_PROPERTY.POSEIDON]: 'poseidon',
  [EXTRA_ODDS_ITEM_PROPERTY.ZEUS]: 'zeus',
}

enum USER_ACTIVITY_TAB_KEY {
  MONTHLY_SCORE = 'monthly-score',
  ALL_SCORE = 'all-score',
}

const MyPage: React.FC = () => {
  const { authStore, routerStore, userStore, globalStore, chattingStore, componentStore, logStore } = useRootStore()
  const [visibleNick, setVisibleNick] = useState(false)
  const [isVisibleMyPageIconBtn, setIsVisibleMyPageIconBtn] = useState(false)
  const [isVisibleMyPageSkin, setIsVisibleMyPageSkin] = useState(false)
  const [themeName, setThemeName] = useState('')
  const [currentTabKey, setCurrentTabKey] = useState(USER_ACTIVITY_TAB_KEY.MONTHLY_SCORE)
  const [visiblePopover, setVisiblePopover] = useState(false)
  const { authInfo, userInfo } = authStore
  const { userDetails, myActivity } = userStore
  const { isChatEnabled } = chattingStore
  const { getUserExtraOddsItemCountByPropertyId } = componentStore

  const isTestAccount = isAccountForTesting(userInfo?.id || '')
  const isFriendInvitationEnabled = globalStore.getFriendInvitationEnabled()
  const maxBetLimit = globalStore.getClubGmoneyMaxBetLimit()
  const isUserBlockEnabled = globalStore.getUserBlockEnabled()
  const majorUserGroupThemeList = globalStore.getMajorUserGroupThemeList()

  // 스크롤 시 베팅권 툴팁이 닫히도록 추가한 코드입니다. [이슈링크](https://task.zempublic.co.kr/view.php?id=9809)
  const handleScroll = () => {
    setVisiblePopover(false)
  }

  useOnMount(() => {
    if (authStore.isSignedIn) {
      const baseMonth = dayjs().format('YYYY-MM')
      userStore.fetchMyActivity(baseMonth).catch(console.error)
      if (!userDetails) {
        fetchUserDetails().catch(console.error)
      }
    } else if (!authStore.isSignedIn) {
      loadOrReload('/')
    }

    if (isMobile) {
      setTimeout(() => {
        appScrollTo(1)
      }, 0)
    }
  })

  useOnUnmount(() => {
    userStore.cleanup()
  })

  useEffect(() => {
    initMajorUserMyPageInfo()
    // 스크롤 시 베팅권 툴팁이 닫히도록 추가한 코드입니다. [이슈링크](https://task.zempublic.co.kr/view.php?id=9809)
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [userDetails])

  const fetchUserDetails = async () => {
    await userStore.getUserDetails({})
  }

  const initMajorUserMyPageInfo = () => {
    if (userDetails?.majorUserInfo) {
      const { majorUserInfo } = userDetails

      const { groupTypeId, myPageIconImageUrl, myPageSkinImageUrl } = majorUserInfo
      const theme = _.find(majorUserGroupThemeList, { groupTypeId })

      setIsVisibleMyPageIconBtn(!_.isEmpty(myPageIconImageUrl))
      setIsVisibleMyPageSkin(!_.isEmpty(myPageSkinImageUrl))
      setThemeName(theme?.themeName || '')
    } else {
      setIsVisibleMyPageIconBtn(false)
      setIsVisibleMyPageSkin(false)
      setThemeName('')
    }
  }

  const onShowNick = () => {
    // 닉네임 변경 창을 표시할려면 userDetails.changeNickItem > -1 로 변경 (기본값 : 0)
    if (userDetails.changeNickItem > 0) {
      setVisibleNick(true)
    } else {
      DialogUtils.showUnableToChangeNickname()
    }
  }
  const onShowSettingLimit = () => {
    logStore.writeButtonLog({ pageId: 'e010', buttonId: '011' }).catch(console.error)
    PopupUtils.showSettingLimitPopup()
    // globalStore.addGeneralPopupProperties(<SettingLimitPopup/>, '손실한도 설정')
  }

  const onClickTermsView = async () => {
    logStore.writeButtonLog({ pageId: 'e010', buttonId: '012' }).catch(console.error)
    let url: string
    if (isMobile) {
      url = `${process.env.HOMEPAGE_MO}/#/cs_policy`
      globalStore.toMoveWebHomePage(url)
    } else {
      url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_private`
      globalStore.toMoveWebHomePage(url)
    }
  }

  const onClickFavoriteUsersButton = () => {
    PopupUtils.showFavoriteManagementPopup()
  }
  const onClickBlockManagementButton = () => {
    logStore.writeButtonLog({ pageId: 'e010', buttonId: '006' }).catch(console.error)
    PopupUtils.showBlockManagementPopup()
  }
  const onClickTelegramButton = () => {
    logStore.writeButtonLog({ pageId: 'e010', buttonId: '007' }).catch(console.error)
    globalStore.setVisibleSideBar(false)
    DialogUtils.showTelegramSetting()
  }

  const onChangeProfile = () => {
    globalStore.setVisibleChangeProfile(true)
    // setVisibleSkin(true)
  }

  const onClickViewProfile = () => {
    // globalStore.toggleProfileImgViewPopupVisible(true)
    DialogUtils.showImagePreview({ imageSrc: userInfo.avatar })
  }

  const availableClickTitle = userInfo.sMemberMark || (!userInfo.isSpecialMember && !_.isNil(userInfo.userTitleId))
  const onClickTitleBtn = () => {
    if (availableClickTitle) {
      DialogUtils.userTitleSetting()
    }
  }

  const onShowMyBank = () => {
    logStore.writeButtonLog({ pageId: 'e010', buttonId: '013' }).catch(console.error)
    PopupUtils.showItemHistoryPopup()
  }

  const getIconType = (type: string, code: string) => {
    let elem = null
    if (type === 'sport') {
      switch (code) {
        case '1':
          elem = <SoccerIco/>
          break
        case '2':
          elem = <BasketballIco/>
          break
        case '3':
          elem = <BaseballIco/>
          break
        case '23':
          elem = <VolleyballIco/>
          break
        case '4':
          elem = <IcehockeyIco/>
          break
        case 'etc':
          elem = <LogoIco/>
          break
      }
    } else {
      switch (code) {
        case '0':
          elem = <CrossIco/>
          break
        case '1':
          elem = <OddsIco/>
          break
        case '2':
          elem = <HandiIco/>
          break
        case '3':
          elem = <LiveIco/>
          break
        case '6':
          elem = <SpecialIco/>
          break
        case '4':
          elem = <LogoIco/>
          break
      }
    }

    return elem
  }

  const onClickCharge = () => {
    logStore.writeButtonLog({ pageId: 'e010', buttonId: '004' }).catch(console.error)
    const url = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/auth_login/${authInfo.authKey}/payment`
    globalStore.toMoveWebHomePage(url, 'self')
    globalStore.resetGeneralPopupProperties()
  }

  let details = null
  if (userDetails) {
    // const jobName = user.job === 2 ? '오즈' : '베터'
    const maxCntSport = _.maxBy(userDetails.sport, 'value')
    // const maxCntMatchup = maxBy(userDetails.matchup, 'value')
    let Sports = null
    if (maxCntSport) {
      Sports = getIconType('sport', maxCntSport.code)
    }
    // let Oztypes = null
    // if (maxCntMatchup) {
    //   Oztypes = getIconType('oztype', maxCntMatchup.code)
    // }

    const sportValue = []
    const sportElem = []
    let count = 1
    for (const sport of userDetails.sport) {
      const typeKey = `MyPage:Sports:TypeWrap:${count}`
      const typeBoxKey = `MyPage:Sports:TypeBox:${count}`
      sportValue.push(
        <span className="chart-item-value" key={typeKey} style={{ flex: sport.value, background: sport.color }}/>,
      )
      sportElem.push(
        <li className="chart-info--item" key={typeKey}>
          <div className="chart-info-spot" key={typeBoxKey} style={{ background: sport.color }}/>
          <p className="chart-info-name">{sport.name}</p>
        </li>
        ,
      )
      count += 1
      // sport.name / sport.color
    }
    const matchupValue = []
    const matchupElem = []
    count = 1
    for (const matchup of userDetails.matchup) {
      const typeKey = `MyPage:MatchUp:TypeWrap:${count}`
      const typeBoxKey = `MyPage:MatchUp:TypeBox:${count}`
      matchupValue.push(
        <span className="chart-item-value" key={typeKey} style={{ flex: matchup.value, background: matchup.color }}/>,
      )
      matchupElem.push(
        <li className="chart-info--item" key={typeKey}>
          <div className="chart-info-spot" key={typeBoxKey} style={{ background: matchup.color }}/>
          <p className="chart-info-name match-type-txt">{matchup.name}</p>
        </li>
        ,
      )
      count += 1
    }

    const onClickLogoutButton = () => {
      logStore.writeButtonLog({ pageId: 'e010', buttonId: '009' }).catch(console.error)
      StorageUtils.removeAuthKey()
      authStore.logout().then(() => {
        if (authStore.memberInfo && !authStore.memberInfo.account) {
          const android = window.android
          if (android) {
            if (android) {
              android.jnGoogleSignOut()
            }
          }
        }
      })
    }

    const onClickMyInfoButton = () => {
      logStore.writeButtonLog({ pageId: 'e010', buttonId: '008' }).catch(console.error)
      const url = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/auth_login/${authInfo?.authKey}/mypage`
      globalStore.toMoveWebHomePage(url)
      globalStore.resetGeneralPopupProperties()
    }

    const onClickRetireButton = () => {
      logStore.writeButtonLog({ pageId: 'e010', buttonId: '010' }).catch(console.error)
      const url = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/auth_login/${authInfo?.authKey}/retire`
      globalStore.toMoveWebHomePage(url)
      globalStore.resetGeneralPopupProperties()
    }

    const popoverTitle = (
      <p
        className="popover-title-txt">{formatNumber(tooltipTicketCount(BET_TICKET_TYPE.BET_TICKET1, userInfo.ticket))}<GmoneyIco/> 보관
        중입니다.</p>
    )
    const popoverContent = (
      <p className="popover-content-txt">10,000<GmoneyIco/> 가 되는 경우 베팅권 1개로 변환됩니다.</p>
    )
    const classNameTicket = userInfo.ticket > 0 ? '' : 'adv-hidden'

    const onClickFriendsInvitationHandler = () => {
      logStore.writeButtonLog({ pageId: 'e010', buttonId: '003' }).catch(console.error)
      if (isMobile) {
        loadOrReload('/friends-invitation')
      } else {
        PopupUtils.showFriendsInvitationPopup()
        globalStore.setVisibleSideBar(false)
      }
    }

    const getItemCountStyleClass = (propertyId: number) => {
      return GOD_BLESS_ITEM_STYLE_CLASS_MAP?.[propertyId] ?? ''
    }

    const items = componentStore.makeUserExtraOddsItems()

    let cardItem: React.ReactElement
    if (_.isEmpty(items)) {
      cardItem = <AdvEmpty ico={EMPTY_ICON.DIVIDEND_CARD}/>
    } else {
      cardItem = (
        <ul className={isMobile ? 'user-dividend-card-list' : 'user-dividend-card-list pc-list'}>
          {items.map(item =>
            <Fragment key={item.key}>
              <li className="user-dividend-card-list--item">
                <img src={`${item.imgBaseUrl}${item.myPageImgUrl}`} alt={item.name}/>
                <span className={`card-quantity ${getItemCountStyleClass(item.propertyId)}`}>
                  x{getUserExtraOddsItemCountByPropertyId(item.propertyId)}</span>
              </li>
            </Fragment>,
          )}
        </ul>
      )
    }

    // let rankImg
    // let noRank = false
    // let rankLabel = ''
    // switch (myActivity.monthlyActivityRank) {
    //   case ACTIVITY_RANK_TYPE.S_RANK:
    //     rankImg = <img src={RatingSImg} alt="S등급" className="rating-img"/>
    //     rankLabel = 'S'
    //     break
    //   case ACTIVITY_RANK_TYPE.A_RANK:
    //     rankImg = <img src={RatingAImg} alt="A등급" className="rating-img"/>
    //     rankLabel = 'A'
    //     break
    //   case ACTIVITY_RANK_TYPE.B_RANK:
    //     rankImg = <img src={RatingBImg} alt="B등급" className="rating-img"/>
    //     rankLabel = 'B'
    //     break
    //   case ACTIVITY_RANK_TYPE.C_RANK:
    //     rankImg = <img src={RatingCImg} alt="C등급" className="rating-img"/>
    //     rankLabel = 'C'
    //     break
    //   case ACTIVITY_RANK_TYPE.D_RANK:
    //     rankImg = <img src={RatingDImg} alt="D등급" className="rating-img"/>
    //     rankLabel = 'D'
    //     break
    //   case ACTIVITY_RANK_TYPE.N_RANK:
    //     rankLabel = 'N'
    //     rankImg = null
    //     noRank = true
    //     break
    // }

    const userActivityTabList = [
      {
        title: (
          <Fragment>
            <span className="txt">활동 점수</span>
          </Fragment>
        ),
        name: '활동점수',
        key: USER_ACTIVITY_TAB_KEY.MONTHLY_SCORE,
      },
      {
        title: (
          <Fragment>
            <span className="txt">전체 이력</span>
          </Fragment>
        ),
        name: '전체이력',
        key: USER_ACTIVITY_TAB_KEY.ALL_SCORE,
      },
    ]

    const monthlyUserPickCount = myActivity?.monthlyUserPickCount ?? 0
    const monthlyCommentCount = myActivity?.monthlyCommentCount ?? 0
    const monthlyLikedCount = myActivity?.monthlyLikedCount ?? 0
    const monthlyShareBettingHistoryCount = myActivity?.monthlySharedBettingHistoryCount ?? 0
    const monthlyCheerForTeamCount = myActivity?.monthlyCheerForTeamCount ?? 0
    const monthlyWriteContentCount = monthlyUserPickCount + monthlyCheerForTeamCount

    const totalUserPickCount = myActivity?.totalUserPickCount ?? 0
    const totalCommentCount = myActivity?.totalCommentCount ?? 0
    const totalLikedCount = myActivity?.totalLikedCount ?? 0
    const totalSharedBettingHistoryCount = myActivity?.totalSharedBettingHistoryCount ?? 0
    const totalCheerForTeamCount = myActivity?.totalCheerForTeamCount ?? 0
    const totalWriteContentCount = totalUserPickCount + totalCheerForTeamCount
    const totalActivityValue = totalUserPickCount + totalCommentCount + totalLikedCount
      + totalSharedBettingHistoryCount + totalCheerForTeamCount

    const totalInfo = [
      {
        name: '작성한 글',
        value: totalWriteContentCount,
        percent: (totalWriteContentCount / totalActivityValue) * 100,
        color: '#FFCC00',
        order: 1,
      },
      {
        name: '작성한 댓글',
        value: totalCommentCount,
        percent: (totalCommentCount / totalActivityValue) * 100,
        color: '#26AAB8',
        order: 2,
      },
      {
        name: '좋아한 글',
        value: totalLikedCount,
        percent: (totalLikedCount / totalActivityValue) * 100,
        color: '#253545',
        order: 3,
      },
      {
        name: '공유한 베팅',
        value: totalSharedBettingHistoryCount,
        percent: (totalSharedBettingHistoryCount / totalActivityValue) * 100,
        color: '#99A7B7',
        order: 4,
      },
    ]

    const baseMonth = dayjs().format('YYYY-MM')

    const userMonthlyActivityList = [
      {
        key: 'activity_btn_01',
        text: <Fragment>
          작성한 글
          <span className="count">{monthlyWriteContentCount}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserPostingActivity('이번달 작성한 글', baseMonth)
        },
      },
      {
        key: 'activity_btn_02',
        text: <Fragment>
          작성한 댓글
          <span className="count">{monthlyCommentCount}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserCommentActivity('이번달 댓글', { initTabIndex: 0, baseMonth })
        },
      },
      {
        key: 'activity_btn_03',
        text: <Fragment>
          좋아한 글
          <span className="count">{monthlyLikedCount}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserLikeActivity('이번달 좋아한 글', { baseMonth })
        },
      },
      {
        key: 'activity_btn_04',
        text: <Fragment>
          공유한 베팅
          <span className="count">{monthlyShareBettingHistoryCount}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserShareBettingHistory('이번달 공유한 베팅', { baseMonth })
        },
      },
    ]

    const userActivityList = [
      {
        key: 'activity_btn_01',
        text: <Fragment>
          <p className="txt"><span className="dot user-pick"></span>작성한 글</p>
          <span className="count">{toShortNumber(totalWriteContentCount)}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserPostingActivity('작성한 글')
        },
      },
      {
        key: 'activity_btn_02',
        text: <Fragment>
          <p className="txt"><span className="dot user-comment"></span>작성한 댓글</p>
          <span className="count">{toShortNumber(myActivity?.totalCommentCount)}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserCommentActivity('댓글', { initTabIndex: 0 })
        },
      },
      {
        key: 'activity_btn_03',
        text: <Fragment>
          <p className="txt"><span className="dot user-like"></span>좋아한 글</p>
          <span className="count">{toShortNumber(myActivity?.totalLikedCount)}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserLikeActivity('좋아한 글', {})
        },
      },
      {
        key: 'activity_btn_04',
        text: <Fragment>
          <p className="txt"><span className="dot user-betting-history"></span>공유한 베팅</p>
          <span className="count">{totalSharedBettingHistoryCount}</span>
        </Fragment>,
        onClickBtn: () => {
          PopupUtils.showUserShareBettingHistory('공유한 베팅', {})
        },
      },
      // TODO 기획내용 변경으로 주석처리 (추후 사용될 수도 있음)
      // {
      //   key: 'activity_btn_03',
      //   icon: <CircleCheckIco/>,
      //   text: '최근 적중률',
      //   onClickBtn: () => {
      //     DialogUtils.showUserDetailInfo(userInfo.id)
      //   },
      // },
    ]

    // const onClickRatingInfo = () => {
    //   DialogUtils.showUserActivityRatingInfo(myActivity)
    // }
    //

    const radius = 61
    const { dashOffset, circumference } = makeActivityGageConfig(radius)
    const remainingScore = makeRemainingActivityScore()
    const activityScore = userStore.myActivity?.monthlyActivityScore ?? 0
    const isReceivedAllReward = remainingScore === 0 && activityScore > 0
      && !globalStore.userActivityInfo?.activityRewardAvailable

    const remainingName = classNames('remaining-score', {
      'adv-hidden': globalStore.userActivityInfo?.activityRewardAvailable,
    })
    const progressBarName = classNames('processing', {
      'achieve': globalStore.userActivityInfo?.activityRewardAvailable,
    })

    const activityScoreBtn = (
      <div className="active-score-btn">
        <div className="month-score-wrap">
          <p className="month">{dayjs().format('M')}월</p>
          <div className="score">
            <p className="score-num">{activityScore.toLocaleString()}점</p>
            <ArrowIco/>
          </div>
          <div className="progress-bar-wrap">
            <svg width="150" height="150" xmlns="http://www.w3.org/2000/svg" className="progress-bar">
              <defs>
                <linearGradient id="mypageProgressGradient" x1="0%" y1="0%" x2="120%" y2="70%">
                  <stop offset="0%" style={{ stopColor: '#ea4d07' }}/>
                  <stop offset="100%" style={{ stopColor: '#fbb000' }}/>
                </linearGradient>
              </defs>
              <circle cx="75" cy="75" r={radius} fill="none" className="bg"/>
              <circle cx="75" cy="75" r={radius} fill="none" strokeDasharray={circumference}
                      strokeDashoffset={dashOffset}
                      className={progressBarName}/>
            </svg>
          </div>
        </div>
        {isReceivedAllReward &&
          <p className={remainingName}><span className="receive-all">이번달 모든 보상을 수령하였습니다</span></p>
        }
        {!isReceivedAllReward &&
          <p className={remainingName}>다음 보상까지 <span className="remaining-score-num">{remainingScore}점</span></p>
        }
        {globalStore.userActivityInfo?.activityRewardAvailable &&
          <div className="reward-btn">
            <p className="reward-btn-txt">보상받기</p>
          </div>
        }
      </div>
    )

    const onClickActivityScorePopup = () => {
      logStore.writeButtonLog({ pageId: 'e010', buttonId: '005' }).catch(console.error)
      PopupUtils.showUserActivityScore()
    }

    const donutCartWrap = <div className={totalActivityValue === 0 ? 'adv-hidden' : 'activity-donut-chat-wrap'}>
      <PieChart
        chartId="activityScoreChart"
        data={totalInfo}
        innerRadius={55}
        series={{ name: 'name', value: 'value', color: 'color' }}
        order={{ field: 'order', desc: 'asc' }}
      />
    </div>

    let contents: React.ReactElement
    switch (currentTabKey) {
      case USER_ACTIVITY_TAB_KEY.MONTHLY_SCORE:
        contents = <Fragment>
          <AdvButton
            contents={activityScoreBtn}
            css={activityScoreBtnStyle}
            onClick={onClickActivityScorePopup}
          />
          <ul className="activity-btn-list">
            {userMonthlyActivityList.map(listItem =>
              <li key={listItem.key} className="activity-btn-list--item">
                <AdvButton
                  flex
                  alignCenter
                  height={43}
                  contents={listItem.text}
                  color={'modalSubTxt'}
                  bold
                  className="activity-btn"
                  onClick={listItem.onClickBtn}
                />
              </li>,
            )}
          </ul>
        </Fragment>
        break
      case USER_ACTIVITY_TAB_KEY.ALL_SCORE:
        contents = <Fragment>
          {donutCartWrap}
          <ul className="activity-btn-list all-score">
            {userActivityList.map(listItem =>
              <li key={listItem.key} className="activity-btn-list--item">
                <AdvButton
                  flex
                  alignCenter
                  height={67}
                  contents={listItem.text}
                  txtAlignCenter
                  bold
                  className="activity-btn"
                  onClick={listItem.onClickBtn}
                />
              </li>,
            )}
          </ul>
        </Fragment>
        break
    }

    const onChange = (tab: Models.TabData) => {
      setCurrentTabKey(tab.key as USER_ACTIVITY_TAB_KEY)
    }

    const userActivity = (
      <Fragment>
        {/* TODO 기획내용 변경으로 주석처리 (추후 사용될 수도 있음) */}
        {/*<div className="activity-history">*/}
        {/*  <Button className="activity-rating" onClick={onClickRatingInfo}>*/}
        {/*    {rankImg}*/}
        {/*    <span className={noRank ? 'rating-txt no-rating' : 'rating-txt'}>*/}
        {/*      {noRank ? '등급없음' : `${rankLabel}등급`}*/}
        {/*      <QuestionMarkIco/>*/}
        {/*    </span>*/}
        {/*  </Button>*/}

        {/*  <div className="cumulative-history">*/}
        {/*    <span>누적 댓글 <span className="num">{toShortNumber(totalCommentCount)}</span></span>*/}
        {/*    <span>누적 좋아요 <span className="num">{toShortNumber(myActivity.totalReceivedLikeCount)}</span></span>*/}
        {/*  </div>*/}
        {/*</div>*/}

        <CenterTabWrap className="my-page">
          <Tabs
            tabs={userActivityTabList}
            initialPage={currentTabKey}
            page={currentTabKey}
            onChange={onChange}
          />
        </CenterTabWrap>
        {contents}

      </Fragment>
    )

    const userDetailsWrapClassName = classNames('', {
      'pc-user-details-wrap': !isMobile,
      'the-black-theme': themeName === 'THE_BLACK',
    })

    details = (
      <UserDetailsWrap className={userDetailsWrapClassName}>
        <ul className="user-setting">
          <li className="user-setting--item profile">
            <Button className="profile--btn" onClick={onClickViewProfile}>
              <span className="profile-img">
                {!_.isEmpty(userDetails.majorUserInfo?.myPageProfileBorderLineImageUrl) &&
                  <span className="profile-skin-img">
                    <img src={userDetails.majorUserInfo?.myPageProfileBorderLineImageUrl} alt="" loading="lazy"/>
                  </span>
                }
                {userInfo.customProfileImg ?
                  (<AdvImg src={userInfo.avatar} srcMode={'small'} alt="사진첩 이미지" className="custom-img"/>)
                  :
                  (<AdvImg src={userInfo.avatar} srcMode={'small'} alt="스킨"/>)
                }
              </span>
            </Button>
            <span className="user-setting--item--nickname">
              {isLiveScoreRecommender(authStore?.recommendInfo?.rcode) &&
                <img src={LiveScoreImg} alt="라이브스코어" className="live-score-badge"/>}
              {userInfo.nick}
            </span>
            <Button TP onClick={onClickTitleBtn}
                    className={availableClickTitle ? 'user-title-btn' : 'user-title-btn is-not-title'}>
              <UserTitle
                showSMark={userInfo.sMemberMark}
                isSpecialMember={userInfo.isSpecialMember}
                userTitleId={userInfo.userTitleId}
                userTitleEnabled={userInfo.userTitleEnabled}
                myPageMode={true}
              />
            </Button>
          </li>
          {
            !isTestAccount &&
            <Fragment>
              <li className="user-setting--item nickname">
                <Button className="user-setting-btn" onClick={onChangeProfile}>
                  <ChangeProfileIco/>
                </Button>
                <span className="user-setting--item--txt">프로필 변경</span>
              </li>
              <li className="user-setting--item nickname">
                <Button className="user-setting-btn" onClick={onShowNick}><EditIco/></Button>
                <span className="user-setting--item--txt">닉네임 변경</span>
              </li>
              <li className="user-setting--item item-list">
                <Button className="user-setting-btn" onClick={onShowMyBank}><ItemListIco/></Button>
                <span className="user-setting--item--txt">아이템 내역</span>
              </li>
            </Fragment>
          }
        </ul>

        <ManagedUserButton
          isVisible={isVisibleMyPageIconBtn}
          managedUserMyPageData={{
            iconImg: userDetails.majorUserInfo?.myPageIconImageUrl || null,
            detailPopupImg: userDetails.majorUserInfo?.myPageDetailImageUrl || null,
          }}
        />

        {isFriendInvitationEnabled && componentStore.isPossibleInvitor && (
          <Button TP className="friends-invitation-btn" onClick={onClickFriendsInvitationHandler}>
            <img src={FriendsInvitationBtnImg} alt="친구초대" style={{ width: '100%' }}/>
            <img src={FriendsInvitationBubbleTxtImg} alt="친구 초대하면 최대 1천만G 지급!" className="bubble-img"/>
          </Button>
        )}

        <ul className="user-money">
          <li className="user-money--item">
            <span className="user-money--item--txt">G머니</span>
            <span className="user-money--item--num">{formatNumber(userInfo.money)}<GmoneyIco/></span>
          </li>
          <li className="user-money--item">
            <span className="user-money--item--txt">하트</span>
            <span className="user-money--item--num">{formatNumber(userInfo.heart)}<HeartIco/></span>
          </li>
          <li className="user-money--item">
            <span className="user-money--item--txt">다이아</span>
            <span className="user-money--item--num">{formatNumber(userInfo.dia - userInfo.diaCasino)}<DiaIco/></span>
          </li>

          <li className={`user-money--item ${classNameTicket}`}>
            <span className="user-money--item--txt">베팅권</span>
            <Popover placement="topRight" title={popoverTitle}
                     content={popoverContent} trigger="click"
                     visible={visiblePopover} onVisibleChange={setVisiblePopover}>
              <span
                className="user-money--item--num cursor-pointer">{formatNumberTicketCount(BET_TICKET_TYPE.BET_TICKET1, userInfo.ticket)} 개<PopoverIco/></span>
            </Popover>
          </li>
          <li className="user-money--item">
            <span className="user-money--item--txt">
              캐시
              {
                !isTestAccount &&
                <Button TP onClick={onClickCharge} className="charge-btn">
                  <img src={sCashBtn} alt="s캐시 충전 버튼" style={{ width: '100%' }}/>
                </Button>
              }
            </span>
            <span className="user-money--item--num">{formatNumber(userInfo.cash)}<ScashIco/></span>
          </li>
        </ul>

        <div className="user-activity">
          {userActivity}
        </div>

        <div className="user-dividend-card">
          <h3 className="tit">보유한 신의 가호</h3>
          {cardItem}
        </div>

        <ul className="user-chart">
          <li className="user-chart--item">
            <p className="user-chart-tit">나의 베팅 성향</p>
            <div className="sports-ico">
              {Sports}
            </div>
            <div className="chart-wrap">
              <div className="chart-bar">
                {sportValue}
              </div>
              <ul className="chart-info">
                {sportElem}
              </ul>
            </div>
          </li>
          <li className="user-chart--item">
            <p className="user-chart-tit">나의 베팅 타입</p>
            <div className="chart-wrap">
              <div className="chart-bar">
                {matchupValue}
              </div>
              <ul className="chart-info">
                {matchupElem}
              </ul>
            </div>
          </li>

          {/* ***************** 도넛 차트 ***************** */}
          {/* {나의 베팅 성향} */}
          {/*<PieChart*/}
          {/*  chartId="sportChart"*/}
          {/*  data={userDetails.sport}*/}
          {/*  innerRadius={60}*/}
          {/*  // stroke={{ color: '#fff', width: 1, opacity: 1 }}*/}
          {/*  series={{ name: 'name', value: 'value', color: 'color' }}*/}
          {/*  order={{ field: 'order', desc: 'asc' }}*/}
          {/*/>*/}
          {/*  <ChartTxtWrap className="sports">{sportElem}</ChartTxtWrap>*/}
          {/*{maxCntSport ? (*/}
          {/*  <RateWrap className="left">*/}
          {/*    <RateTitle>{maxCntSport.name}</RateTitle>*/}
          {/*    <RateValue>*/}
          {/*      <Point>{`${maxCntMatchup.rate}`}</Point>%*/}
          {/*    </RateValue>*/}
          {/*  </RateWrap>*/}
          {/*) : null}*/}

          {/* {나의 베팅 타입} */}
          {/*<IconStyle>{Oztypes}</IconStyle>*/}
          {/* 도넛 차트 */}
          {/*<PieChart*/}
          {/*  chartId="mathupChart"*/}
          {/*  data={userDetails.matchup}*/}
          {/*  innerRadius={60}*/}
          {/*  // stroke={{ color: '#fff', width: 1, opacity: 1 }}*/}
          {/*  series={{ name: 'name', value: 'value', color: 'color' }}*/}
          {/*  order={{ field: 'order', desc: 'asc' }}*/}
          {/*/>*/}
          {/*  <ChartTxtWrap>{matchupElem}</ChartTxtWrap>*/}

          {/*{maxCntMatchup ? (*/}
          {/*  <RateWrap className="right">*/}
          {/*    <RateTitle>{maxCntMatchup.name}</RateTitle>*/}
          {/*    <RateValue>*/}
          {/*      <Point>{`${maxCntMatchup.rate}`}</Point>%*/}
          {/*    </RateValue>*/}
          {/*  </RateWrap>*/}
          {/*) : null}*/}

        </ul>

        <Button className="list-btn" onClick={onClickFavoriteUsersButton}>
          관심 유저
          <span className="icon">
            <FavoriteBorderIco/>
          </span>
        </Button>

        {isUserBlockEnabled && (
          <Button className="list-btn" onClick={onClickBlockManagementButton}>
            차단관리
            <span className="icon block-icon">
              <BlockManagementIco/>
            </span>
          </Button>
        )}

        <Button className="list-btn" onClick={onClickTelegramButton}>
          텔레그램 알림
          <span className="icon">
            <TelegramIco/>
          </span>
        </Button>

        <Button key="MyPage:MYPage" className="list-btn" onClick={onClickMyInfoButton}>
          내 정보 수정
          <span className="icon">
            <WrenchIco/>
          </span>
        </Button>

        <Button key="MyPage:Logout" className="list-btn log-out-btn" onClick={onClickLogoutButton}>
          로그아웃
          <span className="icon">
            <LogoutIco/>
          </span>
        </Button>

        <Button key="MyPage:RetirePage" className="list-btn retire-btn" onClick={onClickRetireButton}>
          회원 탈퇴
        </Button>

        <p className="limit-info">
          <span className="txt">
            승부사온라인은 건전한 승부예측 문화를 만들기 위해 노력하고 있습니다. 이를 위해 과도한 게임 이용에 노출된 이용자를 보호하고, 사행화를 방지하기 위한 손실한도와 이용제한 절차가 마련되었습니다.
          </span>
          <table className="txt-table">
            <tbody>
            <tr>
              <th>&#91; 1일 손실한도 &#93;</th>
              <td>10만원 ~ {maxBetLimit / 10000}만원 범위 내로 설정되며, 한도 초과시 6시간동안 이용이 제한됩니다.</td>
            </tr>
            <tr>
              <th>&#91; 이용제한 &#93;</th>
              <td>설정한 기간동안 게임 이용이 불가하며, 설정 즉시 적용됩니다.</td>
            </tr>
            </tbody>
          </table>
          <span className="txt">
            한번 설정 시, 절대 해제할 수 없습니다.
            <Button TP onClick={onShowSettingLimit} className="setting-btn">
              <SettingIco/>
            </Button>
          </span>
          <span className="txt agree-personal">
            <Button TP onClick={onClickTermsView} className="agree-personal-btn">
              개인정보처리방침
              <ArrowIco/>
            </Button>
          </span>
        </p>

        {/* ***************** 추가될수도 있다고 전달받아 남겨두었습니다 ***************** */}
        {/*<Row className="two">*/}
        {/*  <span>*/}
        {/*    <MoneyTxt>최고 적중배당</MoneyTxt>*/}
        {/*    <MoneyWrap>*/}
        {/*      <Money className="num">{userDetails.hitMaxRate}</Money>*/}
        {/*      배*/}
        {/*    </MoneyWrap>*/}
        {/*  </span>*/}
        {/*  <span>*/}
        {/*    <MoneyTxt>최고 적중머니</MoneyTxt>*/}
        {/*    <MoneyWrap>*/}
        {/*      <Money css={TxtLimit}>{formatNumber(userDetails.hitMaxMoney)}</Money>*/}
        {/*      {Gmoney}*/}
        {/*    </MoneyWrap>*/}
        {/*  </span>*/}
        {/*</Row>*/}

      </UserDetailsWrap>
    )
  }

  const renderMyInfoBg = () => {
    if (isVisibleMyPageSkin) {
      return (
        <Fragment>
          <div css={PageBgStyle} className="my-info managed-user-bg">
            <img src={userDetails.majorUserInfo?.myPageSkinImageUrl || MyInfoImg} alt="마이페이지 배경이미지"
                 className="club-portal-bg" loading="lazy"/>
          </div>
        </Fragment>
      )
    }
    return (
      <Fragment>
        <div css={PageBgStyle} className="my-info">
          <img src={MyInfoImg} alt="축구 경기장" className="club-portal-bg" loading="lazy"/>
        </div>
      </Fragment>
    )
  }


  return (
    <Fragment>
      {renderMyInfoBg()}

      {isMobile && (
        <TitleWrap style={{ margin: 0 }} className="title-wrap no-bg modal">
          <BackBtn onClick={() => {
            logStore.writeButtonLog({ pageId: 'e010', buttonId: '002' }).catch(console.error)
            routerStore.goBack()
          }}>
            <ArrowIco/>
          </BackBtn>
        </TitleWrap>
      )}
      {details}

      <NickDrawer visible={visibleNick} setVisible={setVisibleNick}/>
      {isChatEnabled && !authStore.isSpecificClubMember && isMobile && <Chat key="MyPage:Chat"/>}

      {/*TODO: 랭킹 현황을 볼 수 있는 팝업*/}
      <RankingPopupButton/>
      {isMobile && <NavBar key="MyPage:NavBar"/>}
    </Fragment>
  )
}

export default observer(MyPage)
